import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import ytpfJSON from "../../../data/year_to_perc_flip.json";

interface SpecChartProps {
  sliderVal: number;
}

const SpecChart: React.FC<SpecChartProps> = ({ sliderVal }) => {
  const chartRef = useRef<SVGSVGElement | null>(null);
  const width = 600,
    height = 500,
    offset = 50;

  // Parse the JSON data
  const chartData = ytpfJSON.map((d) => ({
    x: d.year,
    y: d.percent_flipped,
  }));

  useEffect(() => {
    const filteredChartData = chartData.filter((d) => d.x <= sliderVal);

    // Set up the SVG container
    d3.select(chartRef.current).selectAll("*").remove();
    const svg = d3
      .select(chartRef.current)
      .attr("width", "200vh") //width + 2*offset)
      .attr("height", height + 2 * offset);

    // Set up scales for x and y axes
    const xScale = d3
      .scaleLinear()
      .domain([d3.min(chartData, (d) => d.x)!, d3.max(chartData, (d) => d.x)!])
      .range([offset, width]);

    const yScale = d3
      .scaleLinear()
      .domain([
        d3.min(chartData, (d) => d.y)! - 5,
        d3.max(chartData, (d) => d.y)! + 5,
      ])
      .range([height, offset]);

    // Set up the line generator
    const line = d3
      .line<{ x: number; y: number }>()
      .x((d) => xScale(d.x))
      .y((d) => yScale(d.y));

    // Append the line to the SVG container
    svg
      .append("path")
      .datum(filteredChartData)
      .attr("fill", "none")
      .attr("stroke", "black")
      .attr("stroke-width", 3)
      .attr("d", line);

    // Put circles on each of the data points
    svg
      .selectAll("circle")
      .data(filteredChartData)
      .enter()
      .append("circle")
      .attr("cx", (d) => xScale(d.x))
      .attr("cy", (d) => yScale(d.y))
      .attr("r", 3);

    // Append x-axis
    svg
      .append("g")
      .attr("transform", `translate(0, ${height})`)
      .call(d3.axisBottom(xScale).ticks(7).tickFormat(d3.format("d")))
      .style("font-size", "12px");;

    // Append y-axis
    svg
      .append("g")
      .attr("transform", `translate(${offset}, 0)`)
      .call(d3.axisLeft(yScale).ticks(7))
      .style("font-size", "12px");

    // Add x-axis title
    svg
      .append("text") // Add a 'text' element for the title
      .attr("x", width / 2) // Center the title horizontally
      .attr("y", height + offset) // Position below the axis
      .style("text-anchor", "middle")
      .text("Year")
      .style("font-size", "20px");

    // Add y-axis title
    svg
      .append("text")
      .attr("transform", "rotate(-90)") // Rotate for vertical orientation
      .attr("y", -5) // Position to the left of the axis
      .attr("x", 0 - (height + offset) / 2) // Center vertically
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .text("Percent Flipped (%)")
      .style("font-size", "20px");
  }, [sliderVal, chartData]);

  return (
    <div className="spec-chart">
      <svg ref={chartRef} />
    </div>
  );
};

export default SpecChart;
